import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const CarrierUsersPage = () => {
  const { hasPermission, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showChangePermissionsModal, setShowChangePermissionsModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showNewPasswordModal, setShowNewPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [userTypes, setUserTypes] = useState([]);
  const [selectedUserTypeId, setSelectedUserTypeId] = useState('');

  // Create User Modal state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserData, setCreateUserData] = useState({
    carrier_id: '',
    user_type_id: '',
    auth_method: 'google_oauth',
    email: '',
    first_name: '',
    last_name: '',
    display_name: '',
    username: '',
    password: '',
    oauth_provider_id: ''
  });

  // Filter and Sort state
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('last_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Check if user can manage users (for write operations)
  const canManageUsers = hasPermission('manager.users');

  // Get API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  // Memoized function to fetch users
  const fetchUsers = useCallback(async () => {
    if (!user?.carrier_id) {
      setError('No carrier associated with your account');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');

      // Build query parameters - always filter by user's carrier
      const params = new URLSearchParams();
      params.append('carrier_ids', user.carrier_id);

      // Add status filter
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      // Add sorting
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const response = await axios.get(`${API_BASE_URL}/admin/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, user?.carrier_id, statusFilter, sortBy, sortOrder]);

  // Memoized function to fetch user types
  const fetchUserTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/admin/user-types`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUserTypes(response.data.user_types || []);
    } catch (err) {
      console.error('Error fetching user types:', err);
      setError('Failed to fetch user types');
    }
  }, [API_BASE_URL]);

  // Effect to fetch users on component mount
  useEffect(() => {
    if (hasPermission('manager.read')) {
      fetchUsers();
    }
  }, [hasPermission, fetchUsers]);

  const handleActionSelect = (action, userRecord) => {
    setSelectedUser(userRecord);
    if (action === 'deactivate') {
      setPendingAction({ type: 'deactivate' });
      setShowConfirmDialog(true);
    } else if (action === 'reactivate') {
      setPendingAction({ type: 'reactivate' });
      setShowConfirmDialog(true);
    } else if (action === 'remove') {
      setPendingAction({ type: 'remove' });
      setShowConfirmDialog(true);
    } else if (action === 'change_permissions') {
      fetchUserTypes();
      setShowChangePermissionsModal(true);
    } else if (action === 'reset_password') {
      setShowResetPasswordModal(true);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('authToken');

      const response = await axios.post(
        `${API_BASE_URL}/admin/users/${selectedUser.user_id}/reset-password`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setNewPassword(response.data.new_password);
        setShowResetPasswordModal(false);
        setShowNewPasswordModal(true);
      }

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
      console.error('Error resetting password:', err);
    }
  };

  const cancelResetPassword = () => {
    setShowResetPasswordModal(false);
    setSelectedUser(null);
  };

  const closeNewPasswordModal = () => {
    setShowNewPasswordModal(false);
    setNewPassword('');
    setSelectedUser(null);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
  };

  const executeAction = async () => {
    if (!selectedUser || !pendingAction) return;

    try {
      const token = localStorage.getItem('authToken');

      if (pendingAction.type === 'deactivate') {
        await axios.put(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
          is_active: false
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else if (pendingAction.type === 'reactivate') {
        await axios.put(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
          is_active: true
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else if (pendingAction.type === 'remove') {
        await axios.delete(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }

      // Refresh the users list
      await fetchUsers();

      // Reset state
      setShowConfirmDialog(false);
      setSelectedUser(null);
      setPendingAction(null);
    } catch (err) {
      setError('Failed to update user');
      console.error('Error updating user:', err);
    }
  };

  const cancelAction = () => {
    setShowConfirmDialog(false);
    setSelectedUser(null);
    setPendingAction(null);
  };

  const handleChangePermissions = async () => {
    if (!selectedUser || !selectedUserTypeId) return;

    try {
      const token = localStorage.getItem('authToken');

      await axios.put(`${API_BASE_URL}/admin/users/${selectedUser.user_id}`, {
        user_type_id: selectedUserTypeId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Refresh the users list
      await fetchUsers();

      // Reset state
      setShowChangePermissionsModal(false);
      setSelectedUser(null);
      setSelectedUserTypeId('');
      setError(null);

    } catch (err) {
      setError('Failed to update user permissions');
      console.error('Error updating user permissions:', err);
    }
  };

  const cancelChangePermissions = () => {
    setShowChangePermissionsModal(false);
    setSelectedUser(null);
    setSelectedUserTypeId('');
  };

  const openCreateUserModal = () => {
    // Set the carrier ID to the user's carrier
    setCreateUserData(prev => ({
      ...prev,
      carrier_id: user.carrier_id
    }));
    fetchUserTypes();
    setShowCreateUserModal(true);
  };

  const handleCreateUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Validate required fields
      const requiredFields = ['email', 'first_name', 'last_name', 'user_type_id'];
      for (const field of requiredFields) {
        if (!createUserData[field]) {
          setError(`${field.replace('_', ' ')} is required`);
          return;
        }
      }

      // Additional validation for local auth
      if (createUserData.auth_method === 'local') {
        if (!createUserData.username) {
          setError('Username is required for local authentication');
          return;
        }
        if (!createUserData.password || createUserData.password.length < 8) {
          setError('Password must be at least 8 characters for local authentication');
          return;
        }
      }

      const response = await axios.post(`${API_BASE_URL}/admin/users`, createUserData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setShowCreateUserModal(false);
        setCreateUserData({
          carrier_id: '',
          user_type_id: '',
          auth_method: 'google_oauth',
          email: '',
          first_name: '',
          last_name: '',
          display_name: '',
          username: '',
          password: '',
          oauth_provider_id: ''
        });
        setError(null);
        // Refresh users list
        await fetchUsers();
        alert('User created successfully!');
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleCreateUserInputChange = (field, value) => {
    setCreateUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const cancelCreateUser = () => {
    setShowCreateUserModal(false);
    setCreateUserData({
      carrier_id: '',
      user_type_id: '',
      auth_method: 'google_oauth',
      email: '',
      first_name: '',
      last_name: '',
      display_name: '',
      username: '',
      password: '',
      oauth_provider_id: ''
    });
    setError(null);
  };

  // Check permissions after hooks
  if (!hasPermission('manager.read')) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-12 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-12 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-red-600">Error</h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchUsers}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <span className="ml-1 text-gray-400">⇅</span>;
    }
    return sortOrder === 'asc' ?
      <span className="ml-1 text-blue-600">↑</span> :
      <span className="ml-1 text-blue-600">↓</span>;
  };

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="w-full mx-auto">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold">User Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Managing users for: <span className="font-semibold">{user?.carrier_name}</span>
            </p>
          </div>
          {canManageUsers && (
            <button
              onClick={openCreateUserModal}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              + Create New User
            </button>
          )}
        </div>

        {/* Filter and Sort Controls */}
        <div className="mb-4 bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Results count */}
            <div className="ml-auto text-sm text-gray-600">
              {users.length} user{users.length !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Login Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('is_active')}
                >
                  <div className="flex items-center">
                    Status
                    <SortIcon column="is_active" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_date')}
                >
                  <div className="flex items-center">
                    Created Date
                    <SortIcon column="created_date" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('last_login')}
                >
                  <div className="flex items-center">
                    Last Login
                    <SortIcon column="last_login" />
                  </div>
                </th>
                {canManageUsers && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((userRecord) => (
                <tr key={userRecord.user_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {userRecord.user_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {userRecord.display_name || `${userRecord.first_name} ${userRecord.last_name}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    {userRecord.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {userRecord.permission}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                    {userRecord['Login Method']}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    {userRecord['Login Method'] === 'local' ? userRecord.username || 'N/A' : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      userRecord.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {userRecord.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    {new Date(userRecord.created_date * 1000).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                    {userRecord.last_login ? new Date(userRecord.last_login * 1000).toLocaleString() : 'N/A'}
                  </td>
                  {canManageUsers && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                      <select
                        className="border border-gray-300 rounded px-3 py-1 text-sm"
                        onChange={(e) => {
                          if (e.target.value) {
                            handleActionSelect(e.target.value, userRecord);
                            e.target.value = '';
                          }
                        }}
                        defaultValue=""
                      >
                        <option value="">Select Action</option>
                        <option value="change_permissions">Change Permissions</option>
                        {userRecord['Login Method'] === 'local' && (
                          <option value="reset_password">Reset Password</option>
                        )}
                        {userRecord.is_active ? (
                          <>
                            <option value="deactivate">Deactivate</option>
                            <option value="remove">Remove</option>
                          </>
                        ) : (
                          <>
                            <option value="reactivate">Reactivate</option>
                            <option value="remove">Remove</option>
                          </>
                        )}
                      </select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No users found in your organization.</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Action</h3>

            {pendingAction?.type === 'deactivate' && (
              <p className="text-gray-600 mb-6">
                Are you sure you want to deactivate the user "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}"?
                <br /><br />
                This will prevent them from logging in but will preserve their account data.
              </p>
            )}

            {pendingAction?.type === 'reactivate' && (
              <p className="text-gray-600 mb-6">
                Are you sure you want to reactivate the user "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}"?
                <br /><br />
                This will allow them to log in again and access the system.
              </p>
            )}

            {pendingAction?.type === 'remove' && (
              <p className="text-gray-600 mb-6">
                Are you sure you want to remove the user "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}"?
                <br /><br />
                <span className="text-red-600 font-semibold">This action will deactivate the user account and cannot be easily undone.</span>
              </p>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelAction}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                className={`px-4 py-2 text-white rounded hover:opacity-90 ${
                  pendingAction?.type === 'remove'
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
              >
                {pendingAction?.type === 'remove'
                  ? 'Remove User'
                  : pendingAction?.type === 'reactivate'
                    ? 'Reactivate User'
                    : 'Deactivate User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Confirmation Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reset Password</h3>

            <p className="text-gray-600 mb-6">
              Are you sure you want to reset the password for "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}"?
              <br /><br />
              A new secure 8-character password will be generated and shown to you. You will need to share this password with the user.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelResetPassword}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Password Display Modal */}
      {showNewPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Password Reset Successful</h3>

            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                The password for "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}" has been reset.
              </p>

              <div className="bg-yellow-50 border border-yellow-400 rounded p-4 mb-4">
                <p className="text-yellow-800 font-semibold mb-2">⚠️ Important Warning</p>
                <p className="text-yellow-700 text-sm">
                  This is the only time you will see this password. Make sure to copy it now and share it with the user.
                </p>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newPassword}
                  readOnly
                  className="flex-1 border border-gray-300 rounded px-3 py-2 bg-gray-50 font-mono text-lg"
                />
                <button
                  onClick={copyToClipboard}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  title="Copy to clipboard"
                >
                  Copy
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={closeNewPasswordModal}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Permissions Modal */}
      {showChangePermissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Change User Permissions</h3>

            <p className="text-gray-600 mb-4">
              Change permissions for "{selectedUser?.display_name || `${selectedUser?.first_name} ${selectedUser?.last_name}`}":
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Permission: <span className="font-semibold">{selectedUser?.permission}</span>
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Permission *
              </label>
              <select
                value={selectedUserTypeId}
                onChange={(e) => setSelectedUserTypeId(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
                required
              >
                <option value="">Select New Permission</option>
                {userTypes.map((type) => (
                  <option key={type.user_type_id} value={type.user_type_id}>
                    {type.type_name} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelChangePermissions}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePermissions}
                disabled={!selectedUserTypeId}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Update Permissions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create New User for {user?.carrier_name}</h3>
            
            <div className="space-y-4">
              {/* Authentication Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Authentication Method
                </label>
                <select
                  value={createUserData.auth_method}
                  onChange={(e) => handleCreateUserInputChange('auth_method', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="google_oauth">Google OAuth</option>
                  <option value="local">Username/Password</option>
                </select>
              </div>

              {/* User Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Type *
                </label>
                <select
                  value={createUserData.user_type_id}
                  onChange={(e) => handleCreateUserInputChange('user_type_id', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                >
                  <option value="">Select User Type</option>
                  {userTypes.map((type) => (
                    <option key={type.user_type_id} value={type.user_type_id}>
                      {type.type_name} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={createUserData.email}
                  onChange={(e) => handleCreateUserInputChange('email', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="user@example.com"
                  required
                />
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  value={createUserData.first_name}
                  onChange={(e) => handleCreateUserInputChange('first_name', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="John"
                  required
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={createUserData.last_name}
                  onChange={(e) => handleCreateUserInputChange('last_name', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Doe"
                  required
                />
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={createUserData.display_name}
                  onChange={(e) => handleCreateUserInputChange('display_name', e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="Leave empty to use First + Last Name"
                />
              </div>

              {/* Username (only for local auth) */}
              {createUserData.auth_method === 'local' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username *
                  </label>
                  <input
                    type="text"
                    value={createUserData.username}
                    onChange={(e) => handleCreateUserInputChange('username', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="username"
                    required
                  />
                </div>
              )}

              {/* Password (only for local auth) */}
              {createUserData.auth_method === 'local' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={createUserData.password}
                    onChange={(e) => handleCreateUserInputChange('password', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                  />
                </div>
              )}

              {/* OAuth Provider ID (only for OAuth) */}
              {createUserData.auth_method === 'google_oauth' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google OAuth Provider ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={createUserData.oauth_provider_id}
                    onChange={(e) => handleCreateUserInputChange('oauth_provider_id', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Will be set automatically on first login if left empty"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Leave empty to allow the system to set this automatically when the user first logs in with Google.
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={cancelCreateUser}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierUsersPage;
